import React, { FC, useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ValidationStatus from '@components/EntityDrawers/components/inputs/ValidationStatus';
import ProjectRightDrawer from '@components/EntityDrawers/components/ProjectRightDrawer';
import {
  RelationshipDependencyOptions,
  RelationshipTypesOptions,
} from '@components/EntityDrawers/constants/common';
import RelationshipContext from '@components/EntityDrawers/drawers/Relationship/components/RelationshipContext';
import { RelationContextType } from '@components/EntityDrawers/drawers/Relationship/constants/types';
import {
  defaultValues,
  RelationshipFormValues,
  validationSchema,
} from '@components/EntityDrawers/drawers/Relationship/form';
import useContextOptions from '@components/EntityDrawers/drawers/Relationship/hooks/useContextOptions';
import useContextType from '@components/EntityDrawers/drawers/Relationship/hooks/useContextType';
import useFormReset from '@components/EntityDrawers/drawers/Relationship/hooks/useFormReset';
import useIsInitiativeRelation from '@components/EntityDrawers/drawers/Relationship/hooks/useIsInitiativeRelation';
import useCurrentANDRelations from '@components/EntityDrawers/hooks/useCurrentANDRelations';
import useDrawerGate from '@components/EntityDrawers/hooks/useDrawerGate';
import useDrawerInfo from '@components/EntityDrawers/hooks/useDrawerIfo';
import useSaveRelationship from '@components/EntityDrawers/hooks/useSaveRelationship';
import useWeightValueController from '@components/EntityDrawers/hooks/useWeightValueController';
import ControlledSwitch from '@components/Inputs/controllers/ControlledSwitch';
import ControlledTextField from '@components/Inputs/controllers/ControlledTextField';
import ControlledTextFieldSelect from '@components/Inputs/controllers/ControlledTextFieldSelect';
import { DatePicker } from '@components/Inputs/DatePicker';
import useStore from '@components/MainStage/store';
import { ProjectDrawerTypes } from '@constants/entities/drawers';
import { DependencyTypes } from '@constants/entities/relationship';
import { useProject } from '@context/Project/ProjectProvider';
import useProjectDrawerChanges from '@context/Project/useProjectDrawerChanges';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/material';
import dayjs from 'dayjs';

type Props = {
  type:
    | ProjectDrawerTypes.AddRelationship
    | ProjectDrawerTypes.EditRelationship;
};

const RelationshipDrawerBody: FC<Props> = ({ type }) => {
  const { handleCloseDrawer, viewOnly } = useProject();

  const activeEdge = useStore((s) => s.edges.find((e) => e.selected));

  const { open, editMode, drawerTitle, badge } = useDrawerInfo({
    type,
    editType: ProjectDrawerTypes.EditRelationship,
    newTitle: 'Add new relationship',
    editTitle: 'Edit relationship',
    insightsTitle: 'Relationship metadata',
  });

  const form = useForm<RelationshipFormValues>({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    watch,
    formState: { isDirty },
  } = form;

  useProjectDrawerChanges(isDirty, open);
  const { dependency, context } = watch();

  const { isLastANDAuto, isFirstANDAuto } = useCurrentANDRelations(
    dependency as DependencyTypes,
    editMode,
  );

  const contextType = useContextType();
  const { parentOptions, childOptions } = useContextOptions(contextType);

  const presentChildOptions =
    contextType === RelationContextType.FunctionTechnology
      ? true
      : !!childOptions.length;

  const hasEmptyRequirement =
    contextType !== RelationContextType.FunctionTechnology &&
    Object.values(context ?? {}).some(
      (value) => Array.isArray(value) && !value.length,
    );

  useFormReset({
    form,
    open,
    editMode,
    isLastANDAuto,
    contextType,
    parentOptions,
  });

  useWeightValueController(form, open, editMode);

  const { handleSave, isLoading } = useSaveRelationship({
    editMode,
    form,
    formSync: {
      isLastANDAuto,
      contextType,
      parentOptions,
    },
  });

  const handleSaveClick = useCallback(
    async (opts?: { fromShortcut?: boolean }) => {
      const valid = await form.trigger();
      if (!valid) return;

      await handleSave(opts);
    },
    [form, handleSave],
  );

  const weightTypeText = useMemo(() => {
    if (isLastANDAuto) {
      return 'Weight modification is disabled since this is the last auto-assigned AND relationship';
    }

    if (isFirstANDAuto) {
      return 'Weight modification is disabled until another AND relationship is added';
    }

    return '';
  }, [isLastANDAuto, activeEdge?.id, isFirstANDAuto, dependency]);

  const { isInitiativeRelation } = useIsInitiativeRelation();

  const handleClose = useCallback(() => {
    handleCloseDrawer();
  }, [handleCloseDrawer]);

  return (
    <FormProvider {...form}>
      <ProjectRightDrawer
        title={drawerTitle}
        onClose={handleClose}
        disabledSave={
          isLoading || (editMode && !isDirty) || hasEmptyRequirement
        }
        onSave={handleSaveClick}
        badge={badge}
      >
        <ControlledTextFieldSelect
          name="type"
          label="Relationship type"
          disabled={viewOnly}
          required
          options={RelationshipTypesOptions}
        />

        <ControlledTextField
          name="description"
          label="Relationship description"
          placeholder="Relationship description"
          disabled={viewOnly}
          multiline
        />

        {contextType && !!parentOptions.length && presentChildOptions && (
          <RelationshipContext
            type={contextType}
            parentOptions={parentOptions}
            childOptions={childOptions}
          />
        )}

        <ControlledTextFieldSelect
          name="dependency"
          label="Relationship dependency"
          disabled={isLastANDAuto || viewOnly || isInitiativeRelation}
          required
          helperText={
            isLastANDAuto
              ? 'Changing relationship dependency is disabled. This is the last auto-assigned AND relationship. To update dependency, set an auto-assigned weight for another AND relationship from the parent Entity first.'
              : ''
          }
          options={RelationshipDependencyOptions}
        />

        <Stack gap={4}>
          <ControlledTextField
            name="weight"
            label="Relationship weight in %"
            placeholder="Relationship weight"
            required
            helperText={
              watch('weight_manual')
                ? ''
                : 'The system has predefined this weight. Modifying it will impact the weight of other "and" relationships'
            }
            InputProps={{ readOnly: !watch('weight_manual') }}
            disabled={viewOnly}
          />

          <ControlledSwitch
            name="weight_manual"
            label="Modify weight"
            helperText={weightTypeText}
            disabled={isFirstANDAuto || isLastANDAuto || viewOnly}
          />
        </Stack>

        <ValidationStatus name="valid_status" disabledAll />

        {editMode && (
          <>
            <DatePicker
              readOnly
              label="Date created"
              value={dayjs(activeEdge?.data?.dto?.created_at)}
            />

            <DatePicker
              readOnly
              label="Date reviewed"
              placeholder=" "
              value={
                activeEdge?.data?.dto?.reviewed_at
                  ? dayjs(activeEdge?.data?.dto?.reviewed_at)
                  : null
              }
            />
          </>
        )}
      </ProjectRightDrawer>
    </FormProvider>
  );
};

export const RelationshipDrawer: FC<Props> = ({ type }) => {
  const open = useDrawerGate(type, ProjectDrawerTypes.EditRelationship);

  if (!open) return null;

  return <RelationshipDrawerBody type={type} />;
};
